"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Checkbox } from "@/components/ui/checkbox"
import { toast } from "@/components/ui/use-toast"
import { Calendar, Clock, MapPin, AlertCircle, Users, Plus, Trash2, Search, Baby } from "lucide-react"
import { format, isAfter, differenceInYears } from "date-fns"
import { ptBR } from "date-fns/locale"
import { supabaseClient } from "@/lib/supabase/client"
import { getSiteInfoClient } from "@/lib/site-info-client"
import type { Database } from "@/types/supabase"
import { Badge } from "@/components/ui/badge"
import { usePhoneAuth } from "@/components/phone-auth-context"
import PhoneAuthModal from "@/components/phone-auth-modal"
import { useSearchParams } from "next/navigation"
import { validateConfirmationToken } from "@/lib/confirmation-utils"

interface Event {
  id: string
  titulo: string
  descricao: string | null
  data: string
  horario: string
  local: string
  link_maps?: string | null
  link_whatsapp?: string | null
  limite_confirmacoes?: number | null
  data_limite_confirmacao?: string | null
  casamento_id?: string | null
}

interface GuestToConfirm {
  id?: string
  nome: string
  telefone: string
  ddi: string
  isValid: boolean
  isLoading: boolean
}

interface Dependent {
  id: string
  nome: string
  telefone?: string
  data_nascimento?: string
  isConfirmed: boolean
}

type ConfirmacaoStatusRow = {
  evento_id: string
  status_confirmacao: string | null
}

type ConfirmacaoRow = Database["public"]["Tables"]["confirmacoes"]["Row"] & {
  status_confirmacao?: string | null
}

type ConfirmacaoInsert = Database["public"]["Tables"]["confirmacoes"]["Insert"] & {
  status_confirmacao?: string | null
}

type ResponsavelCriancaRow = {
  crianca_id: string
  tipo_responsabilidade?: string | null
  convidados: {
    id: string
    nome: string
    telefone: string | null
    data_nascimento: string | null
  }
}

// Lista de países com DDI
const countries = [
  { code: "+55", name: "Brasil", flag: "🇧🇷" },
  { code: "+1", name: "Estados Unidos", flag: "🇺🇸" },
  { code: "+44", name: "Reino Unido", flag: "🇬🇧" },
  { code: "+33", name: "França", flag: "🇫🇷" },
  { code: "+49", name: "Alemanha", flag: "🇩🇪" },
  { code: "+39", name: "Itália", flag: "🇮🇹" },
  { code: "+34", name: "Espanha", flag: "🇪🇸" },
  { code: "+351", name: "Portugal", flag: "🇵🇹" },
  { code: "+54", name: "Argentina", flag: "🇦🇷" },
  { code: "+56", name: "Chile", flag: "🇨🇱" },
  { code: "+57", name: "Colômbia", flag: "🇨🇴" },
  { code: "+52", name: "México", flag: "🇲🇽" },
]

// Função para formatar telefone brasileiro
const formatBrazilianPhone = (value: string) => {
  const numbers = value.replace(/\D/g, "")
  if (numbers.length <= 2) {
    return numbers
  } else if (numbers.length <= 7) {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`
  } else if (numbers.length <= 11) {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`
  } else {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`
  }
}

// Função para formatar telefone internacional
const formatInternationalPhone = (value: string) => {
  const numbers = value.replace(/\D/g, "")
  if (numbers.length <= 3) {
    return numbers
  } else if (numbers.length <= 6) {
    return `${numbers.slice(0, 3)} ${numbers.slice(3)}`
  } else if (numbers.length <= 10) {
    return `${numbers.slice(0, 3)} ${numbers.slice(3, 6)} ${numbers.slice(6)}`
  } else {
    return `${numbers.slice(0, 3)} ${numbers.slice(3, 6)} ${numbers.slice(6, 10)}`
  }
}

// Função para formatar telefone para validação
const formatPhoneForValidation = (value: string) => {
  return value.replace(/\D/g, "")
}

// Função para calcular idade
const calculateAge = (birthDate: string | undefined) => {
  if (!birthDate) return null
  try {
    const today = new Date()
    const birth = new Date(birthDate)
    return differenceInYears(today, birth)
  } catch (e) {
    return null
  }
}

interface EventListProps {
  initialEvents: Event[]
  initialSiteInfo?: any
}

export default function EventList({ initialEvents, initialSiteInfo }: EventListProps) {
  const { isAuthenticated, guestData, authenticatePhone, authenticateWithGuest } = usePhoneAuth()
  const searchParams = useSearchParams()
  const [events, setEvents] = useState<Event[]>(initialEvents) // Usar eventos iniciais filtrados
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [siteInfo, setSiteInfo] = useState(initialSiteInfo || { corPrimaria: "#e4a99f" })
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [confirmations, setConfirmations] = useState<Record<string, number>>({})
  const [showGroupConfirmation, setShowGroupConfirmation] = useState(false)
  const [isLoadingGuests, setIsLoadingGuests] = useState(false)
  const [userConfirmations, setUserConfirmations] = useState<Set<string>>(new Set())
  const [userConfirmationStatus, setUserConfirmationStatus] = useState<Record<string, string>>({})
  const [confirmedPhones, setConfirmedPhones] = useState<Set<string>>(new Set())
  const [guestsToConfirm, setGuestsToConfirm] = useState<GuestToConfirm[]>([])
  const [selectedGuests, setSelectedGuests] = useState<string[]>([])

  const [attendanceChoice, setAttendanceChoice] = useState<"confirmado" | "nao_vai" | null>(null)

  // Estados para confirmação de pessoas adicionais
  const [showAdditionalGuests, setShowAdditionalGuests] = useState(false)
  const [additionalGuestsCount, setAdditionalGuestsCount] = useState<number>(1)
  const [additionalGuests, setAdditionalGuests] = useState<GuestToConfirm[]>([])

  // Estado para dependentes
  const [dependents, setDependents] = useState<Dependent[]>([])
  const [selectedDependents, setSelectedDependents] = useState<string[]>([])
  const [loadingDependents, setLoadingDependents] = useState(false)
  const [hasDependents, setHasDependents] = useState(false)

  const [isProcessingUrlAuth, setIsProcessingUrlAuth] = useState(false)
  const [urlAuthProcessed, setUrlAuthProcessed] = useState(false)

  useEffect(() => {
    const processUrlAuth = async () => {
      // Só processa uma vez e se não estiver autenticado
      if (urlAuthProcessed || isAuthenticated || isProcessingUrlAuth) return

      const phoneParam = searchParams?.get("phone")
      const tokenParam = searchParams?.get("token")

      if (phoneParam && tokenParam) {
        console.log("Processando autenticação via URL:", { phone: phoneParam, token: tokenParam })
        setIsProcessingUrlAuth(true)

        try {
          // Validar formato do token
          if (!validateConfirmationToken(tokenParam)) {
            console.log("Token inválido:", tokenParam)
            setUrlAuthProcessed(true)
            setIsProcessingUrlAuth(false)
            return
          }

          // Tentar autenticar com o telefone da URL
          const authResult = await authenticatePhone(phoneParam)

          if (authResult.success) {
            console.log("Autenticação via URL bem-sucedida:", authResult.guest?.nome)
            toast({
              title: "Acesso automático realizado!",
              description: `Bem-vindo(a), ${authResult.guest?.nome}!`,
            })
          } else {
            console.log("Falha na autenticação via URL:", authResult.message)
            // Não mostrar toast de erro para não confundir o usuário
            // O modal normal de autenticação será exibido
          }
        } catch (error) {
          console.error("Erro no processamento de autenticação via URL:", error)
        } finally {
          setUrlAuthProcessed(true)
          setIsProcessingUrlAuth(false)
        }
      } else {
        setUrlAuthProcessed(true)
      }
    }

    processUrlAuth()
  }, [searchParams, isAuthenticated, urlAuthProcessed, isProcessingUrlAuth, authenticatePhone])

  useEffect(() => {
    async function loadData() {
      if (!isAuthenticated || !guestData) {
        setEvents([])
        return
      }

      try {
        setIsLoading(true)

        const today = new Date().toISOString().split("T")[0]
        const { data: guestEvents, error } = await supabaseClient
          .from("convidados_eventos")
          .select(`
            evento_id,
            eventos!inner (*)
          `)
          .eq("convidado_id", guestData.id)
          .gte("eventos.data", today)

        if (error) throw error

        const filteredEvents = guestEvents?.map((ge) => ge.eventos).filter(Boolean) || []
        setEvents(filteredEvents)

        // Carregar informações do site se necessário
        if (!initialSiteInfo) {
          const info = await getSiteInfoClient()
          setSiteInfo(info)
        }

        // Verificar se o convidado tem dependentes usando a tabela responsaveis_criancas
        const { data: dependentsCheck, error: dependentsError } = await supabaseClient
          .from("responsaveis_criancas")
          .select("crianca_id")
          .eq("responsavel_id", guestData.id)
          .limit(1)

        if (!dependentsError && dependentsCheck && dependentsCheck.length > 0) {
          setHasDependents(true)
        } else {
          setHasDependents(false)
        }

        // Carregar confirmações
        await loadConfirmations()
        await loadUserConfirmations()
      } catch (error) {
        console.error("Erro ao carregar dados:", error)
        toast({
          title: "Erro",
          description: "Erro ao carregar eventos. Tente novamente.",
          variant: "destructive",
        })
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [isAuthenticated, guestData, initialSiteInfo])

  const loadConfirmations = async () => {
    try {
      const { data } = await supabaseClient.from("confirmacoes").select("evento_id")

      if (data) {
        const counts: Record<string, number> = {}
        data.forEach((item) => {
          counts[item.evento_id] = (counts[item.evento_id] || 0) + 1
        })
        setConfirmations(counts)
      }
    } catch (error) {
      console.error("Erro ao carregar confirmações:", error)
    }
  }

  const loadUserConfirmations = async () => {
    if (!guestData) return

    try {
      const { data } = await supabaseClient
        .from("confirmacoes")
        .select("evento_id, status_confirmacao")
        .eq("convidado_id", guestData.id)
        .returns<ConfirmacaoStatusRow[]>()

      if (data) {
        const confirmedEvents = new Set(data.map((item) => item.evento_id))
        setUserConfirmations(confirmedEvents)

        const statusMap: Record<string, string> = {}
        data.forEach((item) => {
          statusMap[item.evento_id] = item.status_confirmacao || "confirmado"
        })
        setUserConfirmationStatus(statusMap)
      }
    } catch (error) {
      console.error("Erro ao carregar confirmações do usuário:", error)
    }
  }

  const loadConfirmedPhones = async (eventId: string) => {
    try {
      // Buscar todos os telefones que já confirmaram presença neste evento
      const { data } = await supabaseClient
        .from("confirmacoes")
        .select(`
          convidados (telefone)
        `)
        .eq("evento_id", eventId)
        .returns<{ convidados: { telefone: string | null } | null }[]>()

      if (data) {
        const phones = new Set(
          data
            .map((item) => item.convidados?.telefone)
            .filter((phone): phone is string => Boolean(phone)),
        )
        setConfirmedPhones(phones)
      }
    } catch (error) {
      console.error("Erro ao carregar telefones confirmados:", error)
    }
  }

  const loadDependents = async (eventId: string) => {
    if (!guestData?.id) return

    setLoadingDependents(true)
    try {
      // Buscar dependentes diretos
      const { data: dependentsData, error: dependentsError } = await supabaseClient
        .from("responsaveis_criancas")
        .select("crianca_id")
        .eq("responsavel_id", guestData.id)

      if (dependentsError) throw dependentsError

      if (dependentsData && dependentsData.length > 0) {
        console.log("Dependentes encontrados (IDs):", dependentsData)

        // Extrair os IDs dos dependentes
        const dependentIds = dependentsData.map((d) => d.crianca_id)

        // Buscar dados completos dos dependentes
        const { data: dependentDetails, error: detailsError } = await supabaseClient
          .from("convidados")
          .select("id, nome, telefone, data_nascimento")
          .in("id", dependentIds)

        if (detailsError) throw detailsError

        // Verificar quais dependentes já confirmaram presença
        const { data: confirmedDependents, error: confirmedError } = await supabaseClient
          .from("confirmacoes")
          .select("convidado_id")
          .eq("evento_id", eventId)
          .in("convidado_id", dependentIds)

        if (confirmedError) throw confirmedError

        const confirmedDependentIds = new Set(confirmedDependents?.map((d) => d.convidado_id) || [])

        // Formatar dados dos dependentes
        const formattedDependents = (dependentDetails || []).map((dependent) => ({
          id: dependent.id,
          nome: dependent.nome,
          telefone: dependent.telefone || "",
          data_nascimento: dependent.data_nascimento || undefined,
          isConfirmed: confirmedDependentIds.has(dependent.id),
        }))

        setDependents(formattedDependents)
        setSelectedDependents(Array.from(confirmedDependentIds))
        console.log("Dependentes formatados:", formattedDependents)
      } else {
        setDependents([])
        setSelectedDependents([])
        console.log("Nenhum dependente encontrado para o responsável")
      }
    } catch (error) {
      console.error("Erro ao carregar dependentes:", error)
      setDependents([])


      toast({
        title: "Erro",
        description: "Não foi possível carregar os dependentes.",
        variant: "destructive",
      })
    } finally {
      setLoadingDependents(false)
    }
  }

  const syncDependentConfirmations = async (
    eventId: string,
    dependentIds: string[],
    targetStatus: "confirmado" | "nao_vai" = "confirmado",
  ) => {
    if (!guestData?.id || dependents.length === 0) return 0

    const existingConfirmedIds = new Set(dependents.filter((d) => d.isConfirmed).map((d) => d.id))
    const toConfirm = targetStatus === "confirmado" ? dependentIds : []
    const toUnconfirm = targetStatus === "confirmado"
      ? dependents.filter((d) => d.isConfirmed && !dependentIds.includes(d.id)).map((d) => d.id)
      : dependents.filter((d) => d.isConfirmed).map((d) => d.id)

    let processedCount = 0

    for (const dependentId of toConfirm) {
      const dependent = dependents.find((d) => d.id === dependentId)
      if (!dependent) continue

      const { data: existingConfirmation, error: existingError } = await supabaseClient
        .from("confirmacoes")
        .select("id, status_confirmacao")
        .eq("evento_id", eventId)
        .eq("convidado_id", dependentId)
        .maybeSingle()
        .returns<ConfirmacaoRow>()

      if (existingError) {
        console.error("Erro ao verificar confirmação existente do dependente:", existingError)
        throw new Error(`RLS/Permissão ao verificar dependente ${dependent.nome}: ${existingError.message}`)
      }

      let confirmationId: string | null = null

      if (existingConfirmation) {
        if (existingConfirmation.status_confirmacao !== "confirmado") {
          const { error: updateError } = await supabaseClient
            .from("confirmacoes")
            .update({ status_confirmacao: "confirmado" } as ConfirmacaoInsert)
            .eq("id", existingConfirmation.id)

          if (updateError) {
            console.error("Erro ao atualizar confirmação do dependente:", updateError)
            throw new Error(`RLS/Permissão ao confirmar dependente ${dependent.nome}: ${updateError.message}`)
          }
        }

        confirmationId = existingConfirmation.id
      } else {
        const { data: confirmationArray, error: insertError } = await supabaseClient
          .from("confirmacoes")
          .insert({
            convidado_id: dependentId,
            evento_id: eventId,
            status_confirmacao: "confirmado",
            casamento_id: process.env.NEXT_PUBLIC_CASAMENTO_ID || selectedEvent?.casamento_id,
          } as ConfirmacaoInsert)
          .select()
          .returns<ConfirmacaoRow[]>()

        if (insertError) {
          console.error("Erro ao confirmar dependente:", insertError)
          throw new Error(`RLS/Permissão ao criar confirmação do dependente ${dependent.nome}: ${insertError.message}`)
        }

        if (!confirmationArray?.[0]) {
          throw new Error(`Falha ao criar confirmação do dependente ${dependent.nome}`)
        }

        confirmationId = confirmationArray[0].id
      }

      if (!confirmationId) {
        throw new Error(`Falha ao registrar log do dependente ${dependent.nome}`)
      }

      const { error: logError } = await supabaseClient.from("confirmacoes_log").insert({
        confirmacao_id: confirmationId,
        convidado_confirmador_id: guestData.id,
        convidado_confirmado_id: dependentId,
        evento_id: eventId,
        casamento_id: process.env.NEXT_PUBLIC_CASAMENTO_ID || selectedEvent?.casamento_id,
        observacoes: `Dependente ${dependent.nome} confirmado por ${guestData.nome}`,
      })

      if (logError) {
        console.error("Erro ao criar log de dependente:", logError)
      }

      if (!existingConfirmedIds.has(dependentId)) {
        processedCount++
      }
    }

    for (const dependentId of toUnconfirm) {
      const dependent = dependents.find((d) => d.id === dependentId)
      if (!dependent) continue

      const { data: existingConfirmation, error: existingError } = await supabaseClient
        .from("confirmacoes")
        .select("id")
        .eq("evento_id", eventId)
        .eq("convidado_id", dependentId)
        .maybeSingle()
        .returns<ConfirmacaoRow>()

      if (existingError) {
        console.error("Erro ao buscar confirmação do dependente para remoção:", existingError)
        throw new Error(`RLS/Permissão ao encontrar dependente ${dependent.nome}: ${existingError.message}`)
      }

      if (!existingConfirmation?.id) continue

      const { error: updateError } = await supabaseClient
        .from("confirmacoes")
        .update({ status_confirmacao: "nao_vai" } as ConfirmacaoInsert)
        .eq("id", existingConfirmation.id)

      if (updateError) {
        console.error("Erro ao remover confirmação do dependente:", updateError)
        throw new Error(`RLS/Permissão ao desconfirmar dependente ${dependent.nome}: ${updateError.message}`)
      }

      const { error: logError } = await supabaseClient.from("confirmacoes_log").insert({
        confirmacao_id: existingConfirmation.id,
        convidado_confirmador_id: guestData.id,
        convidado_confirmado_id: dependentId,
        evento_id: eventId,
        casamento_id: process.env.NEXT_PUBLIC_CASAMENTO_ID || selectedEvent?.casamento_id,
        observacoes: `Dependente ${dependent.nome} removido da confirmação por ${guestData.nome}`,
      })

      if (logError) {
        console.error("Erro ao criar log de remoção de dependente:", logError)
      }
    }

    return processedCount
  }

  const loadGuestsForEvent = async (eventId: string) => {
    try {
      setIsLoadingGuests(true)
      await loadConfirmedPhones(eventId)

      // Sempre carrega dependentes quando o diálogo de evento abre
      await loadDependents(eventId)
    } catch (error) {
      console.error("Erro ao carregar dados do evento:", error)
    } finally {
      setIsLoadingGuests(false)
    }
  }

  const isEventClosed = (event: Event): { closed: boolean; reason: string } => {
    const today = new Date()

    if (event.data_limite_confirmacao) {
      const limitDate = new Date(event.data_limite_confirmacao + "T00:00:00")
      if (isAfter(today, limitDate)) {
        return { closed: true, reason: "Data limite excedida" }
      }
    }

    if (event.limite_confirmacoes) {
      const currentConfirmations = confirmations[event.id] || 0
      if (currentConfirmations >= (event.limite_confirmacoes || 0)) {
        return { closed: true, reason: "Limite de confirmações atingido" }
      }
    }

    return { closed: false, reason: "" }
  }

  const handleShowAdditionalGuests = () => {
    setShowAdditionalGuests(true)
    setAdditionalGuests(
      Array(additionalGuestsCount)
        .fill(null)
        .map(() => ({ nome: "", telefone: "", ddi: "+55", isValid: false, isLoading: false })),
    )
  }

  const handleAdditionalGuestsCountChange = (count: number) => {
    setAdditionalGuestsCount(count)
    setAdditionalGuests(
      Array(count)
        .fill(null)
        .map(() => ({ nome: "", telefone: "", ddi: "+55", isValid: false, isLoading: false })),
    )
  }

  const handlePhoneChange = (index: number, value: string) => {
    const guest = additionalGuests[index]
    let formattedValue = value

    if (guest.ddi === "+55") {
      formattedValue = formatBrazilianPhone(value)
    } else {
      formattedValue = formatInternationalPhone(value)
    }

    const updated = [...additionalGuests]
    updated[index] = {
      ...updated[index],
      telefone: formattedValue,
      nome: "", // Limpar nome quando telefone muda
      isValid: false, // Resetar validação
      isLoading: false, // Resetar loading
      id: undefined, // Limpar ID
    }
    setAdditionalGuests(updated)
  }

  const handleDdiChange = (index: number, ddi: string) => {
    const guest = additionalGuests[index]
    const cleanPhone = guest.telefone.replace(/\D/g, "")

    let formattedPhone = cleanPhone
    if (ddi === "+55") {
      formattedPhone = formatBrazilianPhone(cleanPhone)
    } else {
      formattedPhone = formatInternationalPhone(cleanPhone)
    }

    const updated = [...additionalGuests]
    updated[index] = {
      ...updated[index],
      ddi: ddi,
      telefone: formattedPhone,
      nome: "", // Limpar nome quando DDI muda
      isValid: false, // Resetar validação
      isLoading: false, // Resetar loading
      id: undefined, // Limpar ID
    }
    setAdditionalGuests(updated)
  }

  const handleSearchGuest = async (index: number) => {
    if (!selectedEvent?.id) {
      toast({
        title: "Selecione um evento",
        description: "Escolha um evento para buscar convidados.",
        variant: "destructive",
      })
      return
    }

    const guest = additionalGuests[index]
    const cleanPhone = guest.telefone.replace(/\D/g, "")
    const ddiNumbers = guest.ddi.replace(/\D/g, "")
    const fullPhone = ddiNumbers + cleanPhone

    if (cleanPhone.length < 8) {
      toast({
        title: "Telefone inválido",
        description: "Digite um número de telefone válido.",
        variant: "destructive",
      })
      return
    }

    const updated = [...additionalGuests]
    updated[index] = { ...updated[index], isLoading: true }
    setAdditionalGuests(updated)

    try {
      // Buscar por telefone exato
      const { data: guestDataArray, error } = await supabaseClient
        .from("convidados")
        .select("id, nome, telefone")
        .eq("telefone", fullPhone)

      if (error) throw error

      const guestData = guestDataArray?.[0]

      if (guestData) {
        // Verificar se está convidado para este evento
        const { data: eventGuestArray, error: eventError } = await supabaseClient
          .from("convidados_eventos")
          .select("convidado_id")
          .eq("evento_id", selectedEvent.id)
          .eq("convidado_id", guestData.id)

        if (eventError) throw eventError

        const eventGuest = eventGuestArray?.[0]

        if (eventGuest) {
          // Verificar se já confirmou presença
          const alreadyConfirmed = confirmedPhones.has(guestData.telefone || "")

          const updatedFinal = [...additionalGuests]
          updatedFinal[index] = {
            ...updatedFinal[index],
            id: guestData.id,
            nome: guestData.nome,
            isValid: !alreadyConfirmed,
            isLoading: false,
          }
          setAdditionalGuests(updatedFinal)

          if (alreadyConfirmed) {
            toast({
              title: "Atenção",
              description: `${guestData.nome} já confirmou presença neste evento.`,
              variant: "destructive",
            })
          } else {
            toast({
              title: "Convidado encontrado!",
              description: `${guestData.nome} foi encontrado na lista de convidados.`,
            })
          }
        } else {
          const updatedFinal = [...additionalGuests]
          updatedFinal[index] = {
            ...updatedFinal[index],
            nome: `${guestData.nome} - NÃO convidado para este evento`,
            isValid: false,
            isLoading: false,
          }
          setAdditionalGuests(updatedFinal)

          toast({
            title: "Convidado não autorizado",
            description: `${guestData.nome} não está convidado para este evento.`,
            variant: "destructive",
          })
        }
      } else {
        const updatedFinal = [...additionalGuests]
        updatedFinal[index] = {
          ...updatedFinal[index],
          nome: "Telefone não encontrado na lista de convidados",
          isValid: false,
          isLoading: false,
        }
        setAdditionalGuests(updatedFinal)

        toast({
          title: "Convidado não encontrado",
          description: "Este telefone não está na lista de convidados.",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error("Erro ao buscar convidado:", error)
      const updatedFinal = [...additionalGuests]
      updatedFinal[index] = {
        ...updatedFinal[index],
        nome: "Erro na busca",
        isValid: false,
        isLoading: false,
      }
      setAdditionalGuests(updatedFinal)

      toast({
        title: "Erro na busca",
        description: "Ocorreu um erro ao buscar o convidado. Tente novamente.",
        variant: "destructive",
      })
    }
  }

  const validateAdditionalGuests = () => {
    const errors: string[] = []

    for (let i = 0; i < additionalGuests.length; i++) {
      const guest = additionalGuests[i]

      if (!guest.telefone.trim()) {
        errors.push(`Telefone da pessoa ${i + 1} é obrigatório`)
        continue
      }

      if (!guest.isValid) {
        errors.push(`Pessoa ${i + 1}: Telefone inválido ou não está na lista de convidados`)
        continue
      }

      if (!guest.nome.trim()) {
        errors.push(`Nome da pessoa ${i + 1} não foi encontrado`)
        continue
      }

      // Verificar duplicatas na lista atual
      const duplicateIndex = additionalGuests.findIndex(
        (g, idx) => idx !== i && formatPhoneForValidation(g.telefone) === formatPhoneForValidation(guest.telefone),
      )
      if (duplicateIndex !== -1) {
        errors.push(`Telefone da pessoa ${i + 1} está duplicado na lista`)
      }
    }

    return errors
  }

  const handleConfirmAttendance = async () => {
    if (!selectedEvent || !guestData || !attendanceChoice) return

    // Validação de casamentoId
    const casamentoIdEnv = process.env.NEXT_PUBLIC_CASAMENTO_ID
    if (casamentoIdEnv && selectedEvent.casamento_id && selectedEvent.casamento_id !== casamentoIdEnv) {
      toast({
        title: "Evento inválido para este site",
        description: "O evento selecionado não pertence ao casamento configurado.",
        variant: "destructive",
      })
      return
    }

    setIsSubmitting(true)

    try {
      // Validar se o convidado está autorizado para este evento
      const { data: guestEventCheck, error: guestEventError } = await supabaseClient
        .from("convidados_eventos")
        .select("convidado_id")
        .eq("convidado_id", guestData.id)
        .eq("evento_id", selectedEvent.id)
        .maybeSingle()

      if (guestEventError || !guestEventCheck) {
        toast({
          title: "Acesso não autorizado",
          description: "Você não está na lista de convidados deste evento.",
          variant: "destructive",
        })
        setIsSubmitting(false)
        return
      }

      const eventStatus = isEventClosed(selectedEvent)
      if (eventStatus.closed) {
        toast({
          title: "Evento fechado para confirmações",
          description: `Este evento não está mais aceitando confirmações. Motivo: ${eventStatus.reason}.`,
          variant: "destructive",
        })
        return
      }

      const { data: existingConfirmations, error: checkError } = await supabaseClient
        .from("confirmacoes")
        .select("id, status_confirmacao")
        .eq("evento_id", selectedEvent.id)
        .eq("convidado_id", guestData.id)
        .returns<ConfirmacaoRow[]>()

      if (checkError) {
        console.error("Erro ao buscar confirmações existentes:", checkError)
        throw new Error(`RLS/Permissão ao buscar confirmações: ${checkError.message}`)
      }

      const existingConfirmation = existingConfirmations?.[0]

      if (existingConfirmation) {
        const { error: updateError } = await supabaseClient
          .from("confirmacoes")
          .update({ status_confirmacao: attendanceChoice } as ConfirmacaoInsert)
          .eq("id", existingConfirmation.id)

        if (updateError) {
          console.error("Erro ao atualizar confirmação:", updateError)
          throw new Error(`RLS/Permissão ao atualizar confirmação: ${updateError.message}`)
        }

        // Criar log da alteração
        const { error: logUpdateError } = await supabaseClient.from("confirmacoes_log").insert({
          confirmacao_id: existingConfirmation.id,
          convidado_confirmador_id: guestData.id,
          convidado_confirmado_id: guestData.id,
          evento_id: selectedEvent.id,
          casamento_id: process.env.NEXT_PUBLIC_CASAMENTO_ID || selectedEvent.casamento_id,
          observacoes: `Alterou resposta de "${existingConfirmation.status_confirmacao}" para "${attendanceChoice}"`,
        })
        
        if (logUpdateError) {
          console.error("Erro ao criar log de alteração (ignorado):", logUpdateError)
          toast({
            title: "Aviso",
            description: "Sua resposta foi salva, mas não foi possível registrar o log de alteração.",
            variant: "default",
          })
        }

        await syncDependentConfirmations(
          selectedEvent.id,
          attendanceChoice === "confirmado" ? selectedDependents : [],
          attendanceChoice,
        )

        // Atualizar estados locais
        setUserConfirmationStatus({
          ...userConfirmationStatus,
          [selectedEvent.id]: attendanceChoice,
        })

        toast({
          title: "Resposta atualizada!",
          description:
            attendanceChoice === "confirmado"
              ? "Sua presença foi confirmada com sucesso."
              : "Registramos que você não poderá comparecer.",
        })

        // Reset states
        setIsDialogOpen(false)
        setSelectedEvent(null)
        setAttendanceChoice(null)
        setShowAdditionalGuests(false)
        setAdditionalGuests([])
        setAdditionalGuestsCount(1)
        setSelectedDependents([])

        // Recarregar confirmações
        await loadConfirmations()
        await loadUserConfirmations()

        return
      }

      const { data: newConfirmationArray, error } = await supabaseClient
        .from("confirmacoes")
        .insert({
          convidado_id: guestData.id,
          evento_id: selectedEvent.id,
          status_confirmacao: attendanceChoice,
          casamento_id: process.env.NEXT_PUBLIC_CASAMENTO_ID || selectedEvent.casamento_id,
        } as ConfirmacaoInsert)
        .select()
        .returns<ConfirmacaoRow[]>()

      if (error) {
        console.error("Erro ao criar confirmação:", error)
        throw new Error(`RLS/Permissão ao criar confirmação: ${error.message}`)
      }

      const newConfirmation = newConfirmationArray?.[0]
      if (!newConfirmation) {
        throw new Error("Falha ao criar confirmação")
      }

      // Criar log da confirmação
      const { error: logError } = await supabaseClient.from("confirmacoes_log").insert({
        confirmacao_id: newConfirmation.id,
        convidado_confirmador_id: guestData.id,
        convidado_confirmado_id: guestData.id,
        evento_id: selectedEvent.id,
        casamento_id: process.env.NEXT_PUBLIC_CASAMENTO_ID || selectedEvent.casamento_id,
        observacoes: attendanceChoice === "confirmado" ? "Confirmação de presença" : "Informou que não vai",
      })
      
      if (logError) {
        console.error("Erro ao criar log de confirmação (ignorado):", logError)
        toast({
          title: "Aviso",
          description: "Presença salva, mas não foi possível registrar o log de confirmação.",
          variant: "default",
        })
      }

      let totalConfirmations = 1

      if (attendanceChoice === "confirmado") {
        // Processar convidados adicionais
        if (showAdditionalGuests && additionalGuests.length > 0) {
          for (const guest of additionalGuests) {
            if (!guest.id || !guest.isValid) continue

            const { data: additionalConfirmationArray, error: confirmError } = await supabaseClient
              .from("confirmacoes")
              .insert({
                convidado_id: guest.id,
                evento_id: selectedEvent.id,
                status_confirmacao: "confirmado",
                casamento_id: process.env.NEXT_PUBLIC_CASAMENTO_ID || selectedEvent.casamento_id,
              } as ConfirmacaoInsert)
              .select()
              .returns<ConfirmacaoRow[]>()

            if (confirmError) {
              console.error("Erro ao criar confirmação adicional:", confirmError)
              throw new Error(`RLS/Permissão ao criar confirmação adicional: ${confirmError.message}`)
            }

            const additionalConfirmation = additionalConfirmationArray?.[0]
            if (!additionalConfirmation) {
              throw new Error(`Falha ao criar confirmação para ${guest.nome}`)
            }

            const { error: logAdditionalError } = await supabaseClient.from("confirmacoes_log").insert({
              confirmacao_id: additionalConfirmation.id,
              convidado_confirmador_id: guestData.id,
              convidado_confirmado_id: guest.id,
              evento_id: selectedEvent.id,
              casamento_id: process.env.NEXT_PUBLIC_CASAMENTO_ID || selectedEvent.casamento_id,
              observacoes: `Confirmação feita por ${guestData.nome} para ${guest.nome}`,
            })

            if (logAdditionalError) {
              console.error("Erro ao criar log de confirmação adicional (ignorado):", logAdditionalError)
              toast({
                title: "Aviso",
                description: "Confirmação adicional salva, mas o log não foi registrado.",
                variant: "default",
              })
            }

            totalConfirmations++
          }
        }

        const dependentCount = await syncDependentConfirmations(selectedEvent.id, selectedDependents, "confirmado")
        totalConfirmations += dependentCount
      }

      if (attendanceChoice === "confirmado") {
        setConfirmations({
          ...confirmations,
          [selectedEvent.id]: (confirmations[selectedEvent.id] || 0) + totalConfirmations,
        })
      }

      setUserConfirmations(new Set([...userConfirmations, selectedEvent.id]))
      setUserConfirmationStatus({
        ...userConfirmationStatus,
        [selectedEvent.id]: attendanceChoice,
      })

      toast({
        title: attendanceChoice === "confirmado" ? "Presença confirmada!" : "Resposta registrada!",
        description:
          attendanceChoice === "confirmado"
            ? "Sua presença foi confirmada com sucesso."
            : "Registramos que você não poderá comparecer.",
      })

      setIsDialogOpen(false)
      setSelectedEvent(null)
      setAttendanceChoice(null)
      setShowAdditionalGuests(false)
      setAdditionalGuests([])
      setAdditionalGuestsCount(1)
      setSelectedDependents([])

      await loadConfirmations()
      await loadUserConfirmations()
    } catch (error) {
      console.error("Erro ao confirmar presença:", error)
      toast({
        title: "Erro ao confirmar presença",
        description: "Não foi possível salvar sua resposta. Tente novamente.",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString + "T00:00:00")
      return format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
    } catch (e) {
      return dateString
    }
  }

  const formatTime = (timeString: string) => {
    try {
      const [hours, minutes] = timeString.split(":")
      const date = new Date()
      date.setHours(Number.parseInt(hours, 10))
      date.setMinutes(Number.parseInt(minutes, 10))
      return format(date, "HH:mm", { locale: ptBR })
    } catch (e) {
      return timeString
    }
  }

  // Se não estiver autenticado e ainda está processando URL, mostrar loading
  if (!isAuthenticated && isProcessingUrlAuth) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-muted-foreground">Processando acesso automático...</p>
        </div>
      </div>
    )
  }

  // Se não estiver autenticado, mostrar modal de autenticação
  if (!isAuthenticated) {
    return (
      <PhoneAuthModal
        isOpen={true}
        onSuccess={(guest) => {
          // Usar o contexto para autenticar diretamente com os dados do guest
          authenticateWithGuest(guest)
        }}
      />
    )
  }

  // Renderizar loading
  if (isLoading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader>
              <div className="h-7 bg-muted animate-pulse rounded w-3/4 mb-2"></div>
              <div className="h-5 bg-muted animate-pulse rounded w-1/2"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="h-5 bg-muted animate-pulse rounded w-1/3"></div>
                <div className="h-5 bg-muted animate-pulse rounded w-1/4"></div>
                <div className="h-5 bg-muted animate-pulse rounded w-1/2"></div>
              </div>
            </CardContent>
            <CardFooter>
              <div className="h-10 bg-muted animate-pulse rounded w-1/3"></div>
            </CardFooter>
          </Card>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Mensagem de boas-vindas */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardContent className="pt-6">
          <p className="text-center text-lg">
            Olá, <strong>{guestData?.nome}</strong>! 👋
          </p>
          <p className="text-center text-muted-foreground mt-2">Aqui estão os eventos.</p>
        </CardContent>
      </Card>

      {events.map((event) => {
        const eventStatus = isEventClosed(event)
        const isEventAvailable = !eventStatus.closed
        const isConfirmed = userConfirmations.has(event.id)
        const currentStatus = userConfirmationStatus[event.id]

        return (
          <Card key={event.id}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <CardTitle>{event.titulo}</CardTitle>
                <div className="flex gap-2">
                  {isConfirmed && currentStatus === "confirmado" && <Badge variant="default">Confirmado</Badge>}
                  {isConfirmed && currentStatus === "nao_vai" && <Badge variant="destructive">Não Vai</Badge>}
                  {!isEventAvailable && <Badge variant="destructive">{eventStatus.reason}</Badge>}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center">
                  <Calendar className="mr-2 h-4 w-4" />
                  <span>{formatDate(event.data)}</span>
                </div>
                <div className="flex items-center">
                  <Clock className="mr-2 h-4 w-4" />
                  <span>{formatTime(event.horario)}</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="mr-2 h-4 w-4" />
                  <span>{event.local}</span>
                </div>
                {event.descricao && (
                  <div className="flex items-center">
                    <AlertCircle className="mr-2 h-4 w-4" />
                    <span>{event.descricao}</span>
                  </div>
                )}
                {event.limite_confirmacoes && (
                  <div className="flex items-center">
                    <Users className="mr-2 h-4 w-4" />
                    <span>
                      {confirmations[event.id] || 0} de {event.limite_confirmacoes} confirmados
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
            <CardFooter>
              <Button
                onClick={() => {
                  setSelectedEvent(event)
                  setSelectedDependents([])
                  setAttendanceChoice(isConfirmed ? (currentStatus as "confirmado" | "nao_vai") : null)
                  loadGuestsForEvent(event.id)
                  setIsDialogOpen(true)
                }}
                disabled={!isEventAvailable && !isConfirmed}
                variant={isConfirmed ? "outline" : "default"}
              >
                {isConfirmed ? "Alterar Resposta" : "Confirmar Presença"}
              </Button>
            </CardFooter>
          </Card>
        )
      })}

      {/* Modal de confirmação */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Responder ao Convite</DialogTitle>
            <DialogDescription>
              {userConfirmations.has(selectedEvent?.id || "")
                ? `Você já respondeu a este convite. Deseja alterar sua resposta para "${selectedEvent?.titulo}"?`
                : `Informe se você poderá comparecer ao evento "${selectedEvent?.titulo}"`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="border rounded-lg p-4 space-y-3">
              <Label className="text-base font-medium">Você vai comparecer ao evento?</Label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="vai-sim"
                    name="attendance"
                    value="confirmado"
                    checked={attendanceChoice === "confirmado"}
                    onChange={(e) => setAttendanceChoice(e.target.value as "confirmado")}
                    className="w-4 h-4"
                  />
                  <label htmlFor="vai-sim" className="text-sm font-medium text-green-700">
                    ✅ Sim, vou comparecer
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="nao-vai"
                    name="attendance"
                    value="nao_vai"
                    checked={attendanceChoice === "nao_vai"}
                    onChange={(e) => setAttendanceChoice(e.target.value as "nao_vai")}
                    className="w-4 h-4"
                  />
                  <label htmlFor="nao-vai" className="text-sm font-medium text-red-700">
                    ❌ Não, não poderei comparecer
                  </label>
                </div>
              </div>
            </div>

            {attendanceChoice === "confirmado" && (
              <>
                <div className="flex items-center space-x-2">
                  <Checkbox id="confirm-self" checked={true} disabled />
                  <label htmlFor="confirm-self" className="text-sm font-medium">
                    {guestData?.nome} (Você)
                  </label>
                </div>

                {/* Seção de dependentes */}
                {loadingDependents ? (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                    <span className="ml-2 text-sm text-muted-foreground">Carregando dependentes...</span>
                  </div>
                ) : dependents.length > 0 ? (
                  <div className="border rounded-md p-4 space-y-3">
                    <h3 className="font-medium text-sm flex items-center">
                      <Baby className="h-4 w-4 mr-2" />
                      Seus dependentes
                    </h3>
                    <div className="space-y-2">
                      {dependents.map((dependent) => {
                        const age = calculateAge(dependent.data_nascimento)
                        return (
                          <div key={dependent.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`dependent-${dependent.id}`}
                              checked={selectedDependents.includes(dependent.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedDependents([...selectedDependents, dependent.id])
                                } else {
                                  setSelectedDependents(selectedDependents.filter((id) => id !== dependent.id))
                                }
                              }}
                            />
                            <label
                              htmlFor={`dependent-${dependent.id}`}
                              className={`text-sm ${dependent.isConfirmed ? "text-muted-foreground" : "font-medium"}`}
                            >
                              {dependent.nome}
                              {age !== null && <span className="ml-1 text-xs text-muted-foreground">({age} anos)</span>}
                              {dependent.isConfirmed && (
                                <span className="ml-2 text-xs text-green-600">(Já confirmado)</span>
                              )}
                            </label>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ) : null}

                {!userConfirmations.has(selectedEvent?.id || "") && !showAdditionalGuests && (
                  <Button variant="outline" onClick={handleShowAdditionalGuests} className="w-full bg-transparent">
                    <Plus className="mr-2 h-4 w-4" />
                    Confirmar + Pessoas
                  </Button>
                )}

                {showAdditionalGuests && (
                  <div className="border-t pt-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <Label>Quantas pessoas adicionais?</Label>
                      <Select
                        value={additionalGuestsCount.toString()}
                        onValueChange={(value) => handleAdditionalGuestsCountChange(Number.parseInt(value))}
                      >
                        <SelectTrigger className="w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                            <SelectItem key={num} value={num.toString()}>
                              {num}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-3">
                      {additionalGuests.map((guest, index) => (
                        <div key={index} className="border rounded-lg p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium">Pessoa {index + 1}</Label>
                            {additionalGuests.length > 1 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const updated = additionalGuests.filter((_, i) => i !== index)
                                  setAdditionalGuests(updated)
                                  setAdditionalGuestsCount(updated.length)
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                          <div>
                            <Label htmlFor={`telefone-${index}`} className="text-xs">
                              Buscar Convidado
                            </Label>
                            <div className="flex gap-2">
                              <Select value={guest.ddi} onValueChange={(value) => handleDdiChange(index, value)}>
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {countries.map((country) => (
                                    <SelectItem key={country.code} value={country.code}>
                                      <div className="flex items-center gap-2">
                                        <span>{country.flag}</span>
                                        <span>{country.code}</span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>

                              <div className="flex-1 flex gap-2">
                                <Input
                                  id={`telefone-${index}`}
                                  placeholder={guest.ddi === "+55" ? "(11) 99999-9999" : "123 456 7890"}
                                  value={guest.telefone}
                                  onChange={(e) => handlePhoneChange(index, e.target.value)}
                                  className="flex-1"
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleSearchGuest(index)}
                                  disabled={guest.isLoading || !guest.telefone.trim()}
                                  className="px-3"
                                >
                                  {guest.isLoading ? (
                                    <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                                  ) : (
                                    <Search className="h-4 w-4" />
                                  )}
                                </Button>
                              </div>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {guest.ddi === "+55"
                                ? "Formato: (DDD) Número (ex: (11) 99999-9999)"
                                : "Formato internacional (ex: 123 456 7890)"}
                            </p>
                          </div>
                          <div>
                            <Label htmlFor={`nome-${index}`} className="text-xs">
                              Nome
                            </Label>
                            <Input
                              id={`nome-${index}`}
                              placeholder="Nome será preenchido automaticamente"
                              value={guest.nome}
                              disabled
                              className={guest.isValid ? "bg-green-50" : ""}
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    <Button variant="outline" size="sm" onClick={() => setShowAdditionalGuests(false)}>
                      Cancelar pessoas adicionais
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmAttendance}
              disabled={isSubmitting || !attendanceChoice}
              variant={attendanceChoice === "nao_vai" ? "destructive" : "default"}
            >
              {isSubmitting
                ? "Processando..."
                : userConfirmations.has(selectedEvent?.id || "")
                  ? "Atualizar Resposta"
                  : attendanceChoice === "confirmado"
                    ? "Confirmar Presença"
                    : attendanceChoice === "nao_vai"
                      ? "Informar que Não Vou"
                      : "Selecione uma opção"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
